<template>
  <div class="border-solid border-0 border-t border-soft p-6 text-sm">

    <validated-input :name="index.toString()"
                     :value="button.title"
                     :validated-value="validatedValue"
                     label="Title"
                     placeholder="Button label"
                     :error-message="showError"
                     :is-autofocus="focus === SECTION_FOCUSES.BUTTON + index"
                     :custom-length-limit="titleCountLimit"
                     @keyup="updateButtonTitle($event.target.value)"
                     @blur="validateTitle"
    />

    <div class="mt-2">
      <label class="font-bold flex flex-col">
        Button Type
        <dropdown-button name="selectButtonType"
                         label="Select button type"
                         :options="buttonOptions"
                         primary-button-variant="secondary"
                         :close-on-select="true"
                         dropdown-position="above-left"
                         :value="button.type"
                         primary-button-classes="justify-right w-40 h-10"
                         class="mt-1"
                         arrow-color="black-a20"
                         @select-button="handleSelectButton"
        />
      </label>

      <div v-if="button.type" class="text-gray mt-2">
        {{ buttonDescription }}
      </div>

      <validated-input v-if="[BUTTON_TYPES.URL, BUTTON_TYPES.PHONE].includes(button.type)"
                       v-model="buttonPayloadValue"
                       :label="payloadLabel"
                       :custom-length-limit="payloadCountLimit"
                       :placeholder="payloadPlaceholder"
                       :error-message="phoneNumberErrorDescription"
                       class="mt-5"
      />

      <with-error-message v-else-if="BUTTON_TYPES.CALLBACK === button.type"
                          :show-error="isCallbackInputError && payloadInputProps.length <= payloadInputProps.max"
                          :character-limit="payloadInputProps.max"
                          :character-count="payloadInputProps.length"
                          class="mt-4 flex flex-col"
      >
        <label class="font-bold">
          {{ payloadLabel }}
        </label>
        <t-textarea :value="button.payload"
                    class="w-full text-gray-dark mt-2"
                    :class="isCallbackInputError ? 'border-danger' : 'border-gray focus:border-focus'"
                    variant="textSection"
                    :placeholder="payloadPlaceholder"
                    @input="updateButtonPayload"
                    rows=2
        />
      </with-error-message>

      <div class="flex" :class="{'mt-6': button.type !== BUTTON_TYPES.CALLBACK, 'mt-16': isPayloadError}">
        <base-button variant="transparent-error"
                     class="mx-auto"
                     :id="`delete-button-${index + 1}`"
                     @click="() => $emit('remove-button')"
        >
          Delete Button
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BUTTON_TYPES, SECTION_FOCUSES } from '@/store/constants'
import { STATE_ID } from '@/utils/bot-json.constants'
import assoc from 'ramda/src/assoc'
import RandomUtils from '@/utils/random.utils'
import channelModule from '@/store/modules/channel.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import previewModule from '@/store/modules/preview.module'
import templateModule from '@/store/modules/template.module'
import answerController from '@/controllers/answer.controller'
import templateController from '@/controllers/template.controller'
import DropdownButton from '@/components/common/DropdownButton'
import dropdownButtonsUtils from '@/utils/dropdown-button.utils'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import ValidatedInput from '@/components/base-components/ValidatedInput.vue'
import { REGEXES } from '@/utils/key-regex.constants'
import variableRefsUtils from '@/utils/variable-refs.utils'

const randomString = RandomUtils.randomString

export default {
  name: 'ButtonEditor',

  components: { ValidatedInput, DropdownButton, WithErrorMessage },

  props: {
    button: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    botId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      BUTTON_TYPES,
      SECTION_FOCUSES,

      oldType: this.button.type,
      typeSelected: this.button.type,

      /** When switching button types, it keeps the right payload so that it won't get removed. */
      payloadBackups: {
        url: null,
        phone: null,
        postback: 'postback_' + randomString(5),
        callback: null
      }
    }
  },

  watch: {
    typeSelected(newType) {
      switch (newType) {
        case BUTTON_TYPES.URL:
          return this.switchButtonToUrl(this.button.payload)
        case BUTTON_TYPES.POSTBACK:
          return this.switchButtonToPostback(this.button.payload)
        case BUTTON_TYPES.PHONE:
          return this.switchButtonToPhone(this.button.payload)
        case BUTTON_TYPES.CALLBACK:
          return this.switchButtonToCallback(this.button.payload)
      }
    }
  },

  computed: {
    actionId: chatBubbleModule.actionId,
    focus: chatBubbleModule.sectionFocus,
    bubbleSelected: chatBubbleModule.bubbleSelected,
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,
    stateTree: chatBubbleModule.stateTree,

    isTitleError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Button label is not allowed to be empty!'
    },

    isNonExistingVariableError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.error?.section.includes('button') && this.errorDescription?.startsWith('Variable')
    },

    showError() {
      return (this.isTitleError || this.isNonExistingVariableError) && this.errorDescription
    },

    isPayloadError() {
      const description = this.errorDescription?.toLowerCase()
      return (
        chatBubbleModule.isErrorBubbleFocused() &&
        (description?.includes('phone') || description?.includes('url') || description?.includes('callback'))
      )
    },

    stateId() {
      return this.bubbleSelected.stateId
    },

    buttonDescription() {
      const buttonDescriptionMap = {
        [BUTTON_TYPES.URL]: 'Opens the link in a new tab.',
        [BUTTON_TYPES.PHONE]: 'Dials the phone number. The best practice is to provide the phone number in the text as well.',
        [BUTTON_TYPES.POSTBACK]: 'Continues in the conversation flow.',
        [BUTTON_TYPES.CALLBACK]: 'Executes a JavaScript callback that your website can react to, e.g. add an item to the shopping basket.'
      }
      return buttonDescriptionMap[this.button.type]
    },

    payloadLabel() {
      switch (this.button.type) {
        case BUTTON_TYPES.URL:
          return 'Link to'
        case BUTTON_TYPES.PHONE:
          return 'Phone number'
        case BUTTON_TYPES.CALLBACK:
          return 'Payload'
      }
    },

    payloadPlaceholder() {
      switch (this.button.type) {
        case BUTTON_TYPES.URL:
          return 'https://website.com'
        case BUTTON_TYPES.PHONE:
          return '+420 123 456 789'
        case BUTTON_TYPES.CALLBACK:
          return 'An identifier for the triggered Callback button.'
      }
    },

    postback() {
      return 'postback_' + this.actionId + '_' + randomString(5)
    },

    previewController() {
      return templateModule.isTemplateEditor() ? templateController : answerController
    },

    titleCount() {
      return this.button.title.length
    },

    titleCountLimit() {
      return channelModule.existsNonAmioChat() ? 20 : 25
    },

    payloadCountLimit() {
      return this.button.type === BUTTON_TYPES.PHONE && !this.buttonPayloadValue.includes('$') ? 20 : null
    },

    buttonOptions() {
      return dropdownButtonsUtils.getButtonTypeOptions()
    },

    payloadInputProps() {
      return {
        max: 1000,
        length: this.button.payload?.length || 0
      }
    },

    isCallbackInputError() {
      return this.payloadInputProps.length > this.payloadInputProps.max || this.isPayloadError
    },

    buttonPayloadValue: {
      get() {
        return this.button.payload || ''
      },
      set(payload) {
        this.updateButtonPayload(payload)
      }
    },

    phoneNumberErrorDescription() {
      return (
        (this.isPayloadError && this.errorDescription) ||
        this.phoneFormatErrorDescription
      )
    },

    phoneFormatErrorDescription() {
      if (
        this.button.type !== BUTTON_TYPES.PHONE ||
        !this.button.payload ||
        this.button.payload.startsWith('$') ||
        REGEXES.PHONE.test(this.button.payload)
      ) {
        return null
      }

      return `
        Please enter a valid phone number starting with a plus sign (+), followed by digits,
        with optional spaces or hyphens in between.
      `
    },

    validatedValue() {
      return variableRefsUtils.substituteVariableRefs(this.button.title)
    }
  },

  methods: {
    handleSelectButton(e) {
      this.typeSelected = this.buttonOptions[e].value
    },

    _backupPayload(oldPayload) {
      switch (this.oldType) {
        case BUTTON_TYPES.URL:
          this.payloadBackups.url = oldPayload
          return
        case BUTTON_TYPES.PHONE:
          this.payloadBackups.phone = oldPayload
          return
        case BUTTON_TYPES.POSTBACK:
          this.payloadBackups.postback = oldPayload
          return
        case BUTTON_TYPES.CALLBACK:
          this.payloadBackups.callback = oldPayload
      }
    },

    _switchButton(oldPayload, button) {
      this._backupPayload(oldPayload)

      if (this.oldType === BUTTON_TYPES.POSTBACK) {
        this.previewController.removePostback(this.button.payload, this.stateTree, templateModule.getTemplateId())
      }

      this.oldType = button.type
      this._updateButton(button)
      if (this.isTitleError) return

      this.error = null
    },

    switchButtonToPhone(oldPayload) {
      this._switchButton(oldPayload, {
        type: BUTTON_TYPES.PHONE,
        title: this.button.title,
        payload: this.payloadBackups.phone
      })
    },

    switchButtonToPostback(oldPayload) {
      const postback = this.payloadBackups.postback
      const postbackButton = {
        type: BUTTON_TYPES.POSTBACK,
        title: this.button.title,
        payload: postback
      }
      this._switchButton(oldPayload, postbackButton)

      this.previewController.addPostback(this.stateId, postback, STATE_ID.REF_GLOBAL_IDLE, templateModule.getTemplateId())
    },

    switchButtonToUrl(oldPayload) {
      this._switchButton(oldPayload, {
        type: BUTTON_TYPES.URL,
        title: this.button.title,
        payload: this.payloadBackups.url
      })
    },

    switchButtonToCallback(oldPayload) {
      const callback = this.payloadBackups.callback
      const callbackButton = {
        type: BUTTON_TYPES.CALLBACK,
        title: this.button.title,
        payload: callback
      }
      this._switchButton(oldPayload, callbackButton)
    },

    _updateButton(button) {
      this.$emit('update-button', button)
    },

    updateButtonTitle(title) {
      const updatedButton = assoc('title', title, this.button)
      this._updateButton(updatedButton)
      if (this.errorDescription?.includes('Please add content')) return

      this.error = null
    },

    updateButtonPayload(payload) {
      const updatedButton = assoc('payload', payload, this.button)
      this._updateButton(updatedButton)
      if (this.isPayloadError) this.error = null
    },

    isTitleAboveLimit(title) {
      const resolvedTitle = variableRefsUtils.substituteVariableRefs(title)
      return resolvedTitle.length > this.titleCountLimit
    },

    validateTitle(e) {
      if (!this.isTitleAboveLimit(e.target.value)) return

      const substitutedTitle = variableRefsUtils.substituteVariableRefs(e.target.value)
      const split = substitutedTitle.length - this.titleCountLimit
      e.target.value = e.target.value.slice(0, -split)
      this.updateButtonTitle(e.target.value)
    }
  }
}
</script>
